import { FC } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import {
  capitalizeFirstLetter,
  colors,
  formatXAxis,
  formatYAxis,
  IAiChart,
  IStackedBarConfig,
} from "./chartConfig";
import { customTokens } from "../../theme/theme";
import { useStyles } from "./AiGraphs.styles";

const AiStackedBarChart: FC<IAiChart> = ({ config, chartData }) => {
  const styles = useStyles();
  const {
    data: { x, y, stack },
  } = config as IStackedBarConfig;

  const xKey = x.field;
  const xLabel = x.label;

  const yKey = y.field;
  const yLabel = y.label;

  // If we have series, pivot data into multiple y-fields
  let transformedData = chartData;
  let stackValues: string[] = [];

  if (stack) {
    stackValues = Array.from(new Set(chartData.map((d) => d[stack.field])));

    transformedData = chartData.reduce((acc: any[], curr) => {
      const existing = acc.find((item) => item[xKey] === curr[xKey]);
      if (existing) {
        existing[curr[stack.field]] = curr[yKey];
      } else {
        acc.push({
          [xKey]: curr[xKey],
          [curr[stack.field]]: curr[yKey],
        });
      }
      return acc;
    }, []);
  }

  return (
    <ResponsiveContainer width="100%" height={400}>
      <BarChart
        data={transformedData}
        margin={{ left: 40 }}
        barGap={0}
        barCategoryGap="0%"
      >
        <CartesianGrid
          stroke={customTokens.colorNeutralBackground4}
          horizontal={true}
          vertical={false}
        />

        <XAxis
          tickFormatter={(v) => formatXAxis(v)}
          dataKey={xKey}
          axisLine={false}
          tickLine={false}
          interval={0}
          label={{
            value: xLabel,
            position: "bottom",
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />

        <YAxis
          tickFormatter={(v) => formatYAxis(v)}
          axisLine={false}
          tickLine={false}
          label={{
            value: yLabel,
            angle: -90,
            position: "insideLeft",
            offset: -20,
            style: {
              textAnchor: "middle",
              fontWeight: 700,
              fill: customTokens.fontDarkGrayBody,
            },
          }}
        />

        <Tooltip formatter={(v) => formatYAxis(v)} />

        <Legend
          wrapperStyle={{ paddingTop: 30 }}
          content={({ payload }) => (
            <div className={styles.legendContainer}>
              {payload?.map((entry, index) => (
                <div key={`item-${index}`} className={styles.legendWrapper}>
                  <span
                    className={styles.legendIcon}
                    style={{ backgroundColor: entry.color }}
                  />
                  <span>{capitalizeFirstLetter(entry.value ?? "")}</span>
                </div>
              ))}
            </div>
          )}
        />

        {stackValues.map((s, idx) => (
          <Bar
            key={s}
            dataKey={s}
            name={s}
            stackId="a"
            fill={colors[idx % colors.length]}
            maxBarSize={40}
          />
        ))}
      </BarChart>
    </ResponsiveContainer>
  );
};

export default AiStackedBarChart;
